import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {TrainingPackageResponse, TrainingRegistrationResponse, HorseResponse, UserEntityRelationWithUserDataResponse, UserResponse} from '../../../../../../../../../../../../@shared/models/response.module';
import {TrainingService} from '../../../../../../../../../../../../@shared/services/training.service';
import {ToastService} from '../../../../../../../../../../../../@core/services/toast.service';
import {Horses} from '../../../../../../../../../../../../@shared/services/horses';
import {CompanyService} from '../../../../../../../../../../../../@shared/services/company.service';
import {AddHorse} from '../../../../../../../../../../../../pages/user-profile/components/user-profile-horses/components/add-horse/add-horse';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {AQH_HOR_0} from '../../../../../../../../../../../../@shared/models/input.module';

@Component({
  selector: 'app-edit-package-registration',
  standalone: false,
  templateUrl: './edit-package-registration.html',
  styleUrl: './edit-package-registration.css'
})
export class EditPackageRegistration implements OnInit {
  @Input() registration!: TrainingRegistrationResponse;
  @Input() package!: TrainingPackageResponse;

  form: FormGroup;
  saving = false;
  loadingHorses = false;
  loadingUsers = false;

  // User search
  availableUsers: UserEntityRelationWithUserDataResponse[] = [];
  filteredUsers: UserEntityRelationWithUserDataResponse[] = [];
  selectedUser: UserResponse | null = null;
  userSearchTerm = '';

  // Horse selection
  availableHorses: HorseResponse[] = [];
  selectedHorse: HorseResponse | null = null;

  // Status fields
  status: 'pending' | 'confirmed' | 'cancelled' = 'pending';
  paymentStatus: 'not_required' | 'pay_later' | 'pending' | 'paid' | 'refunded' = 'pay_later';
  notes = '';

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private trainingService: TrainingService,
    private horsesService: Horses,
    private companyService: CompanyService,
    private toastService: ToastService,
    private modalService: NgbModal
  ) {
    this.form = this.fb.group({
      user_id: ['', Validators.required],
      horse_id: [null]
    });
  }

  ngOnInit() {
    if (this.registration) {
      this.selectedUser = this.registration.user || null;
      this.selectedHorse = this.registration.horse || null;
      this.status = this.registration.status;
      this.paymentStatus = this.registration.payment_status;
      this.notes = this.registration.notes || '';

      // Set initial search term
      if (this.selectedUser) {
        this.userSearchTerm = `${this.selectedUser.first_name} ${this.selectedUser.last_name}`;
      }

      // Set user_id immediately
      this.form.patchValue({
        user_id: this.registration.user_id
      });
    }

    // Load all users and horses
    this.loadUsers();
    this.loadAllHorses();
  }

  loadUsers() {
    this.loadingUsers = true;
    this.companyService.getCompanyEmployees().subscribe({
      next: (users: UserEntityRelationWithUserDataResponse[]) => {
        this.availableUsers = users || [];
        this.filteredUsers = this.availableUsers;
        this.loadingUsers = false;
      },
      error: () => {
        this.loadingUsers = false;
        this.availableUsers = [];
        this.filteredUsers = [];
        this.toastService.error('Failed to load users');
      }
    });
  }

  loadAllHorses() {
    this.loadingHorses = true;
    const payload: AQH_HOR_0 = {};

    this.horsesService.getHorses(payload).subscribe({
      next: (horses: HorseResponse[]) => {
        this.availableHorses = horses || [];
        this.loadingHorses = false;

        // Set the current horse_id after horses are loaded
        if (this.registration && this.registration.horse_id) {
          setTimeout(() => {
            this.form.patchValue({
              horse_id: this.registration.horse_id
            }, { emitEvent: true });

            this.selectedHorse = this.availableHorses.find(h => h.horse_id === this.registration.horse_id) || this.selectedHorse;
            this.form.get('horse_id')?.updateValueAndValidity();
          }, 100);
        }
      },
      error: () => {
        this.loadingHorses = false;
        this.availableHorses = [];
        this.toastService.error('Failed to load horses');
      }
    });
  }

  onUserSearch() {
    const searchTerm = this.userSearchTerm.toLowerCase().trim();

    if (!searchTerm) {
      this.filteredUsers = this.availableUsers;
      return;
    }

    this.filteredUsers = this.availableUsers.filter(user => {
      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
      const email = user.email?.toLowerCase() || '';
      return fullName.includes(searchTerm) || email.includes(searchTerm);
    });
  }

  selectUser(user: UserEntityRelationWithUserDataResponse) {
    this.selectedUser = user as any;
    this.form.patchValue({ user_id: user.user_id });
    this.userSearchTerm = `${user.first_name} ${user.last_name}`;
    this.filteredUsers = [];
  }

  clearUserSearch() {
    this.userSearchTerm = '';
    this.selectedUser = null;
    this.form.patchValue({ user_id: '' });
    this.filteredUsers = [];
  }

  onHorseChanged(horseId: any) {
    this.form.patchValue({ horse_id: horseId });
    this.selectedHorse = this.availableHorses.find(h => h.horse_id === horseId) || null;
  }

  openAddHorseModal() {
    if (!this.selectedUser) {
      this.toastService.warning('Please select a user first');
      return;
    }

    const modalRef = this.modalService.open(AddHorse, { size: 'lg', centered: true });

    modalRef.result.then((newHorse: HorseResponse) => {
      if (newHorse) {
        this.toastService.success('Horse added successfully');
        this.loadAllHorses();
      }
    }, () => {});
  }

  close() {
    this.activeModal.dismiss();
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;

    const payload = {
      registration_id: this.registration.registration_id,
      status: this.status,
      payment_status: this.paymentStatus,
      horse_id: this.form.get('horse_id')?.value || null,
      notes: this.notes
    };
    console.log('Saving registration with payload:', payload);

    this.trainingService.updateRegistration(payload).subscribe({
      next: () => {
        this.toastService.success('Registration updated successfully');
        this.activeModal.close(true);
      },
      error: (err) => {
        this.saving = false;
        this.toastService.error(err.error?.reason || 'Failed to update registration');
      }
    });
  }

  formatPrice(priceCents: number, currency: string): string {
    return `${(priceCents / 100).toFixed(2)} ${currency}`;
  }

  get userIdInvalid(): boolean {
    const c = this.form.get('user_id');
    return !!(c && c.invalid && c.touched);
  }
}
